/**
 * Cart drawer behavior (sections/cart-drawer.liquid).
 *
 * - Header cart links open the drawer instead of navigating (the /cart
 *   href remains the no-JS fallback).
 * - Add-to-cart flows (PDP AJAX handler, quick-add's `cart:added` event)
 *   refresh + open it.
 * - Quantity and remove actions post to /cart/change.js, then the drawer
 *   re-fetches its own section via the Section Rendering API so all
 *   money formatting stays server-side.
 * - Focus is trapped while open; Escape closes; updates are announced
 *   via a polite live region.
 *
 * Exposes window.CartDrawer = { open, close, refresh, onAdded }.
 */
(function() {
  'use strict';

  var root = document.getElementById('CartDrawerRoot');
  if (!root) return; // catalog mode locked, or section missing

  var panel = root.querySelector('.cart-drawer');
  var content = root.querySelector('[data-cart-drawer-content]');
  var countLabel = root.querySelector('[data-cd-count-label]');
  var lastFocus = null;
  var liveRegion = null;

  function announce(message) {
    if (!liveRegion) {
      liveRegion = document.createElement('span');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = message;
  }

  function isOpen() {
    return root.classList.contains('is-open');
  }

  function syncCounts() {
    var count = parseInt(content.getAttribute('data-cart-count') || '0', 10);
    if (countLabel) countLabel.textContent = '(' + count + ')';
    document.querySelectorAll('.cart-count').forEach(function(el) {
      el.textContent = count;
      el.classList.toggle('hidden-count', count === 0);
    });
  }

  function open() {
    if (isOpen()) return;
    lastFocus = document.activeElement;
    root.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    var closeBtn = root.querySelector('.cart-drawer__close');
    if (closeBtn) closeBtn.focus();
  }

  function close() {
    if (!isOpen()) return;
    root.classList.remove('is-open');
    document.body.style.overflow = '';
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    lastFocus = null;
  }

  function refresh() {
    root.classList.add('is-busy');
    return fetch('/?sections=cart-drawer', { headers: { Accept: 'application/json' } })
      .then(function(res) { return res.json(); })
      .then(function(data) {
        var html = data['cart-drawer'];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-drawer-content]');
        if (fresh) {
          content.innerHTML = fresh.innerHTML;
          content.setAttribute('data-cart-count', fresh.getAttribute('data-cart-count') || '0');
          syncCounts();
        }
      })
      .catch(function(err) {
        console.error('Cart drawer refresh failed:', err);
      })
      .then(function() {
        root.classList.remove('is-busy');
      });
  }

  function onAdded() {
    return refresh().then(function() {
      open();
      announce('Item added. Cart updated.');
    });
  }

  function changeLine(line, quantity) {
    root.classList.add('is-busy');
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function(res) { return res.json(); })
      .then(function() {
        announce(quantity === 0 ? 'Item removed from cart' : 'Cart updated');
        return refresh();
      })
      .catch(function(err) {
        console.error('Cart update failed:', err);
        root.classList.remove('is-busy');
        return refresh();
      });
  }

  document.addEventListener('click', function(e) {
    var opener = e.target.closest('.cart-page-link');
    if (opener) {
      e.preventDefault();
      open();
      refresh();
      return;
    }

    if (!root.contains(e.target)) return;

    if (e.target.closest('[data-cart-drawer-close]')) {
      // "Browse products" carries this attr too: let its navigation happen
      if (!e.target.closest('a')) e.preventDefault();
      close();
      return;
    }

    var qtyWrap = e.target.closest('.cart-drawer__qty');
    if (qtyWrap) {
      var line = parseInt(qtyWrap.getAttribute('data-line'), 10);
      var qtyEl = qtyWrap.querySelector('[data-cd-qty]');
      var current = parseInt(qtyEl ? qtyEl.textContent : '1', 10) || 1;
      if (e.target.closest('[data-cd-plus]')) changeLine(line, current + 1);
      if (e.target.closest('[data-cd-minus]')) changeLine(line, Math.max(0, current - 1));
      return;
    }

    var removeBtn = e.target.closest('[data-cd-remove]');
    if (removeBtn) {
      changeLine(parseInt(removeBtn.getAttribute('data-line'), 10), 0);
    }
  });

  document.addEventListener('keydown', function(e) {
    if (!isOpen()) return;
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (e.key === 'Tab') {
      var focusables = panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  document.addEventListener('cart:added', function() {
    onAdded();
  });

  window.CartDrawer = {
    open: open,
    close: close,
    refresh: refresh,
    onAdded: onAdded
  };
})();
